import { QueryClient, QueryFunction } from "@tanstack/react-query";

// 🚀 EIGENE ULTRA-PERSISTENT BROWSER-CACHE LÖSUNG
const CACHE_VERSION = '1.0';
const CACHE_KEY = 'follw-query-cache';

// Lade gespeicherte Cache-Daten aus localStorage
const loadPersistedCache = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const data = JSON.parse(cached);
      if (data.version === CACHE_VERSION) {
        console.log('💾 PERSISTENT CACHE GELADEN - Daten verfügbar!');
        return data.cache;
      }
    }
  } catch (error) {
    console.log('⚠️ Cache-Laden fehlgeschlagen:', error);
  }
  return undefined;
};

// Speichere Cache-Daten in localStorage mit robuster Fehlerbehandlung
const persistCache = (cache: any) => {
  try {
    // Filtere nur wichtige Queries für den Cache (stark reduziert)
    const filteredCache = cache.filter((query: any) => {
      const key = query.queryKey?.[0];
      return key && (
        key.includes('/api/auth/me') ||
        key.includes('/api/projects')
      );
    }).slice(0, 10); // Maximal 10 Queries

    const cacheData = {
      version: CACHE_VERSION,
      cache: filteredCache,
      timestamp: Date.now()
    };

    const serialized = JSON.stringify(cacheData);
    
    // Prüfe Größe des Cache-Objekts
    if (serialized.length > 5000000) { // 5MB Limit
      console.log('⚠️ Cache zu groß - überspringe Speicherung');
      return;
    }

    localStorage.setItem(CACHE_KEY, serialized);
    // console.log('💾 CACHE STORED PERSISTENTLY!');
  } catch (error) {
    console.log('⚠️ Cache-Speichern fehlgeschlagen:', error);
    
    // Bei Speicher-Problemen: Lösche alten Cache und versuche erneut
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      try {
        localStorage.removeItem(CACHE_KEY);
        console.log('🗑️ Cache-Speicher geleert wegen Überlauf');
      } catch (clearError) {
        console.log('⚠️ Cache-Löschung fehlgeschlagen:', clearError);
      }
    }
  }
};

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    // Try to parse as JSON to extract the message field
    let errorMessage = text;
    let errorCode: string | undefined;
    try {
      const errorJson = JSON.parse(text);
      errorMessage = errorJson.message || text;
      errorCode = errorJson.error || errorJson.code;
    } catch {
      // If not JSON, use the text as-is
      errorMessage = text;
    }
    if (errorCode === 'SUBSCRIPTION_READ_ONLY' && typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (!url.pathname.startsWith('/settings') || url.searchParams.get('tab') !== 'subscription') {
        window.location.href = '/settings?tab=subscription';
      }
    }
    const error = new Error(errorMessage);
    (error as any).status = res.status;
    (error as any).responseText = text;
    (error as any).code = errorCode;
    throw error;
  }
}

export async function apiRequest(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
  
  // Get current language from localStorage (set by i18n) or default to 'en'
  let language = 'en';
  if (typeof window !== 'undefined') {
    const storedLang = localStorage.getItem('i18nextLng');
    if (storedLang) {
      language = storedLang.split('-')[0]; // Extract base language (e.g., 'en' from 'en-US')
    } else if (navigator.language) {
      language = navigator.language.split('-')[0];
    }
  }
  
  // Don't set Content-Type for FormData - let the browser set it automatically
  const isFormData = options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'Accept-Language': language,
    ...options.headers,
  };

  const apiBase = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:3000' : '');
  const fullUrl = `${apiBase}${url}`;

  const res = await fetch(fullUrl, {
    credentials: "include", // Express-Session über Cookies
    ...options,
    headers,
  });

  if (res.status === 401 && !url.includes('/auth/')) {
    // Lösche lokale User-Daten bei Authentifizierungsfehlern
    localStorage.removeItem('userCode');
    localStorage.removeItem('userId');
    localStorage.removeItem('userName');
    // Sofortiger Redirect zur Login-Seite
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey.join("/") as string;
    const apiBase = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:3000' : '');
    const fullUrl = `${apiBase}${url}`;
    
    const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    
    const res = await fetch(fullUrl, {
      credentials: "include", // Express-Session über Cookies
      headers,
    });

    if (res.status === 401) {
      if (unauthorizedBehavior === "returnNull") {
        return null;
      }
      if (!url.includes('/auth/')) {
        // Lösche lokale User-Daten bei Authentifizierungsfehlern
        localStorage.removeItem('userCode');
        localStorage.removeItem('userId');
        localStorage.removeItem('userName');
        // Sofortiger Redirect zur Login-Seite
        window.location.href = '/login';
      }
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Nicht bei jedem Mount neu laden
      refetchOnReconnect: false, // Nicht bei Internet-Reconnect neu laden
      staleTime: 30 * 60 * 1000, // 30 MINUTEN Cache - MEGA PERSISTENT!
      gcTime: 60 * 60 * 1000, // 1 STUNDE im Memory behalten
      retry: 1, // Ein Retry für bessere UX
      // Background-Updates für frische Daten ohne Loading
      refetchIntervalInBackground: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// 🚀 EINFACHE PERSISTENCE-LÖSUNG: Cache-Events abfangen
queryClient.getQueryCache().subscribe(() => {
  // Reduziertes auto-save - nur bei wichtigen Änderungen
  clearTimeout((window as any).cacheTimeout);
  (window as any).cacheTimeout = setTimeout(() => {
    persistCache(queryClient.getQueryCache().getAll());
  }, 5000); // Verlängert auf 5 Sekunden statt 1 Sekunde
});

// 🚀 BROWSER-CLOSE: Speichere Cache automatisch
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    persistCache(queryClient.getQueryCache().getAll());
  });

  // 🚀 AUTO-SAVE: Alle 5 Minuten speichern (reduziert von 2 Minuten)
  setInterval(() => {
    persistCache(queryClient.getQueryCache().getAll());
  }, 5 * 60 * 1000);
}
